import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import type { ContainedItem, ItemView, StoryLanguage } from "@/lib/games/roleplay/contract";
import { categoryLabel, freshnessLabel, wearLabel } from "./labels";

// What the player holds (31), each item with its short description and, by its kind, its state: information only. An
// item is used through the player's words, never by clicking it here (30). A broken item stays in the list with its
// wear beside it (78). `note` sits in the header's corner, where the tutorial's guide marks its example inventory as
// one. `fresh` names the items the player has just got: each wears a neutral "new" beside its name until the player's
// next turn. The game page holds whether it is open (`open`, `onToggle`; folded when a game begins), the guide's
// example opens by itself. It opens and closes like every Disclosure, on the side boxes' one surface, the story box's
// and Studio's (22).

const NOTHING_FRESH: ReadonlySet<string> = new Set();

// A charge with a capacity reads as a battery in percent; a charge without one stays a number. A
// device with any charge left never reads 0 % (23.6), since 0 % means it no longer works (29).
function batteryPercent(charge: number, capacity: number): number {
  const percent = Math.min(100, Math.max(0, Math.round((charge * 100) / capacity)));
  return charge > 0 ? Math.max(1, percent) : percent;
}

function ItemFacts({ item }: { item: ContainedItem }) {
  const intl = useIntl();
  const facts = [
    categoryLabel(intl, item.category),
    item.usesLeft === null
      ? null
      : intl.formatMessage(
          { id: "grow.roleplay.inventory.usesLeft", defaultMessage: "{count, plural, one {usable once} other {usable # times}}" },
          { count: item.usesLeft },
        ),
    item.charge === null
      ? null
      : item.capacity === null || item.capacity <= 0
        ? intl.formatMessage({ id: "grow.roleplay.inventory.charge", defaultMessage: "Charge {charge}" }, { charge: item.charge })
        : intl.formatMessage(
            { id: "grow.roleplay.inventory.battery", defaultMessage: "Battery {percent}%" },
            { percent: batteryPercent(item.charge, item.capacity) },
          ),
    item.freshness === null ? null : freshnessLabel(intl, item.freshness),
    item.condition === null ? null : wearLabel(intl, item.condition),
  ].filter((fact): fact is string => fact !== null);
  return <p className="smycles-field-meta text-ink-muted">{facts.join(" · ")}</p>;
}

function Item({ item, language, fresh }: { item: ContainedItem; language: StoryLanguage; fresh: boolean }) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <p className="text-smycles-body font-medium">{item.name[language]}</p>
        {fresh ? (
          <span className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="grow.roleplay.inventory.new" defaultMessage="new" />
          </span>
        ) : null}
      </div>
      <p className="smycles-field-meta text-ink-muted">{item.description[language]}</p>
      <ItemFacts item={item} />
    </>
  );
}

export function InventoryPanel({
  inventory,
  language,
  note,
  fresh = NOTHING_FRESH,
  open,
  onToggle,
}: {
  inventory: readonly ItemView[];
  language: StoryLanguage;
  note?: ReactNode;
  fresh?: ReadonlySet<string>;
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <Disclosure
      title={<FormattedMessage id="grow.roleplay.inventory.title" defaultMessage="Inventory" />}
      count={inventory.length || null}
      headerAction={note ? <span className="smycles-field-meta text-ink-muted">{note}</span> : undefined}
      defaultOpen
      open={open}
      onToggle={onToggle}
      className="bg-surface"
    >
      {inventory.length === 0 ? (
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="grow.roleplay.inventory.empty" defaultMessage="You are not carrying anything." />
        </p>
      ) : (
        <ul className="space-y-3">
          {inventory.map((item) => (
            <li key={item.id} className="space-y-0.5">
              <Item item={item} language={language} fresh={fresh.has(item.id)} />
              {item.contents.length > 0 ? (
                <ul className="mt-2 space-y-2 border-l border-ink/10 pl-3">
                  {item.contents.map((contained) => (
                    <li key={contained.id} className="space-y-0.5">
                      <Item item={contained} language={language} fresh={fresh.has(contained.id)} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Disclosure>
  );
}
